import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import bwip from 'bwip-js';

pdfMake.addVirtualFileSystem(pdfFonts);

const NAVY = '#1f3864';
const NAVY_LIGHT = '#2e4a7a';
const GREY_BG = '#f1f5f9';

const DELIVERY_LABELS = {
    'kurier': 'Kurier',
    'paczkomaty': 'Paczkomaty',
    'paleta': 'Paleta',
    'polpaleta': 'Półpaleta',
    'dowoz': 'Dowóz',
    'brak': 'Brak / do ustalenia',
    'odbior osobisty': 'Odbiór osobisty',
    'kurier klienta': 'Kurier klienta'
};

function fmtDate(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pl-PL');
}

function fmtMoney(v) {
    if (v == null || isNaN(v)) return '0,00';
    return Number(v).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Kwota słownie (PLN) – część całkowita słownie + grosze jako XX/100.
function amountInWords(value) {
    const zl = Math.floor(Math.abs(value || 0));
    const gr = Math.round((Math.abs(value || 0) - zl) * 100);

    const jed = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć'];
    const nas = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
    const dzi = ['', '', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
    const set = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset'];

    function tripletToWords(n) {
        let out = [];
        const h = Math.floor(n / 100);
        const t = Math.floor((n % 100) / 10);
        const u = n % 10;
        if (h) out.push(set[h]);
        if (t === 1) { out.push(nas[u]); }
        else { if (t) out.push(dzi[t]); if (u) out.push(jed[u]); }
        return out.join(' ').trim();
    }

    function groupName(idx, n) {
        // idx: 0 = jednostki, 1 = tysiące, 2 = miliony
        if (idx === 0) return '';
        const last = n % 10;
        const last2 = n % 100;
        if (idx === 1) {
            if (n === 1) return 'tysiąc';
            if (last >= 2 && last <= 4 && !(last2 >= 12 && last2 <= 14)) return 'tysiące';
            return 'tysięcy';
        }
        if (idx === 2) {
            if (n === 1) return 'milion';
            if (last >= 2 && last <= 4 && !(last2 >= 12 && last2 <= 14)) return 'miliony';
            return 'milionów';
        }
        return '';
    }

    let words = '';
    if (zl === 0) {
        words = 'zero';
    } else {
        const groups = [];
        let rest = zl;
        while (rest > 0) { groups.push(rest % 1000); rest = Math.floor(rest / 1000); }
        const parts = [];
        for (let i = groups.length - 1; i >= 0; i--) {
            if (groups[i] === 0) continue;
            const w = tripletToWords(groups[i]);
            const gn = groupName(i, groups[i]);
            parts.push((w + ' ' + gn).trim());
        }
        words = parts.join(' ').replace(/\s+/g, ' ').trim();
    }

    const grStr = String(gr).padStart(2, '0');
    return words + ' zł ' + grStr + '/100';
}

function makeQrDataUrl(url) {
    try {
        const canvas = document.createElement('canvas');
        bwip.toCanvas(canvas, {
            bcid: 'qrcode',
            text: url,
            scale: 4,
            padding: 2
        });
        return canvas.toDataURL('image/png');
    } catch (e) {
        return null;
    }
}

function sectionHeader(text) {
    return {
        table: {
            widths: ['*'],
            body: [[{ text, color: '#ffffff', bold: true, fontSize: 10, margin: [6, 3, 6, 3] }]]
        },
        layout: { fillColor: () => NAVY, hLineWidth: () => 0, vLineWidth: () => 0 },
        margin: [0, 8, 0, 4]
    };
}

function labelValueStack(rows) {
    const stack = [];
    rows.forEach(r => {
        stack.push({ text: r[0], fontSize: 8, color: '#6b7280', margin: [0, 0, 0, 0] });
        stack.push({ text: r[1] || '—', fontSize: 10, bold: !!r[2], margin: [0, 0, 0, 6] });
    });
    return { stack };
}

/**
 * Generuje PDF "Karta Realizacji Zamówienia" na podstawie zamówienia (ZO).
 * @param {object} order - pełny obiekt zamówienia (z getOrder: customer, cart.product, computed)
 * @param {object} options - { docUrl, issuerName, cardNumber }
 * @returns {Promise<Blob>}
 */
export function generateOrderRealizationCardPdf(order, options = {}) {
    const customer = order.customer || {};
    const totals = (order.computed && order.computed.totalPrice) ? order.computed.totalPrice : { netto: 0, brutto: 0 };
    const currency = order.currency || 'PLN';
    const docUrl = options.docUrl || '';
    const qrDataUrl = docUrl ? makeQrDataUrl(docUrl) : null;

    // Pozycje
    const itemsBody = [[
        { text: 'Lp.', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'center' },
        { text: 'Produkt / Opis', bold: true, fillColor: GREY_BG, fontSize: 8 },
        { text: 'Ilość', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'right' },
        { text: 'J.m.', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'center' },
        { text: 'Cena netto', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'right' },
        { text: 'Wartość netto', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'right' },
        { text: 'VAT %', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'right' },
        { text: 'Wartość VAT', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'right' },
        { text: 'Wartość brutto', bold: true, fillColor: GREY_BG, fontSize: 8, alignment: 'right' }
    ]];

    let sumNetto = 0, sumVat = 0, sumBrutto = 0;
    (order.cart || []).forEach((pos, idx) => {
        const product = pos.product || {};
        const name = [product.code, product.name].filter(Boolean).join(' — ') || product.code || '(produkt)';
        const desc = pos.note ? (name + '\n' + pos.note) : name;
        const qty = pos.qty || 0;
        const price = pos.price || 0;
        const rate = (pos.taxRate != null ? pos.taxRate : 0.23);
        const netto = qty * price;
        const vat = netto * rate;
        const brutto = netto + vat;
        sumNetto += netto; sumVat += vat; sumBrutto += brutto;

        itemsBody.push([
            { text: String(idx + 1), alignment: 'center', fontSize: 8 },
            { text: desc, fontSize: 8 },
            { text: String(qty), alignment: 'right', fontSize: 8 },
            { text: 'szt', alignment: 'center', fontSize: 8 },
            { text: fmtMoney(price), alignment: 'right', fontSize: 8 },
            { text: fmtMoney(netto), alignment: 'right', fontSize: 8 },
            { text: Math.round(rate * 100) + '%', alignment: 'right', fontSize: 8 },
            { text: fmtMoney(vat), alignment: 'right', fontSize: 8 },
            { text: fmtMoney(brutto), alignment: 'right', fontSize: 8 }
        ]);
    });

    itemsBody.push([
        { text: 'RAZEM', colSpan: 5, bold: true, alignment: 'right', fontSize: 8 }, {}, {}, {}, {},
        { text: fmtMoney(sumNetto), bold: true, alignment: 'right', fontSize: 8 },
        { text: '', },
        { text: fmtMoney(sumVat), bold: true, alignment: 'right', fontSize: 8 },
        { text: fmtMoney(sumBrutto), bold: true, alignment: 'right', fontSize: 8 }
    ]);

    const nettoTotal = totals.netto || sumNetto;
    const bruttoTotal = totals.brutto || sumBrutto;
    const vatTotal = bruttoTotal - nettoTotal;

    const customerAddress = [customer.street, [customer.postCode, customer.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

    const docDefinition = {
        pageSize: 'A4',
        pageMargins: [30, 30, 30, 30],
        content: [
            // Nagłówek
            {
                columns: [
                    {
                        width: '*',
                        stack: [
                            { text: 'MARSS FILTRY Sp. z o.o.', fontSize: 20, bold: true, color: '#111827' },
                            { text: 'KARTA REALIZACJI ZAMÓWIENIA', fontSize: 13, bold: true, color: NAVY, margin: [0, 2, 0, 0] }
                        ]
                    },
                    {
                        width: 160,
                        table: {
                            widths: ['*'],
                            body: [
                                [{ text: 'NR KARTY', fontSize: 8, alignment: 'center', fillColor: GREY_BG }],
                                [{ text: options.cardNumber || order.name || order.code || '—', fontSize: 12, bold: true, alignment: 'center' }]
                            ]
                        },
                        layout: { hLineColor: () => NAVY, vLineColor: () => NAVY }
                    }
                ]
            },

            // Sekcje 1-3 w trzech kolumnach
            {
                columns: [
                    {
                        width: '34%',
                        stack: [
                            sectionHeader('1. INFORMACJE O ZAMÓWIENIU'),
                            labelValueStack([
                                ['Nr ZAM (klient)', order.clientNo || '—'],
                                ['Nr ZO ERP', order.name || order.code || '—', true],
                                ['Data wystawienia', fmtDate(new Date())],
                                ['Wystawił', options.issuerName || '—']
                            ])
                        ]
                    },
                    {
                        width: '33%',
                        stack: [
                            sectionHeader('2. NABYWCA'),
                            { text: customer.name || '(brak)', fontSize: 11, bold: true, margin: [0, 0, 0, 4] },
                            ...(customerAddress ? [{ text: customerAddress, fontSize: 9, margin: [0, 0, 0, 4] }] : []),
                            ...(customer.taxNo ? [{ text: 'NIP: ' + customer.taxNo, fontSize: 9, margin: [0, 0, 0, 4] }] : []),
                            ...(customer.phone ? [{ text: 'Tel.: ' + customer.phone, fontSize: 9, margin: [0, 0, 0, 2] }] : []),
                            ...(customer.email ? [{ text: 'E-mail: ' + customer.email, fontSize: 9 }] : [])
                        ]
                    },
                    {
                        width: '33%',
                        stack: [
                            sectionHeader('3. WARUNKI HANDLOWE'),
                            labelValueStack([
                                ['Termin realizacji', order.hardDeadline ? fmtDate(order.hardDeadline) : (order.customerDate ? fmtDate(order.customerDate) : 'do ustalenia')],
                                ['Warunki dostawy', DELIVERY_LABELS[order.deliveryType] || order.deliveryType || '—'],
                                ['Płatność', order.deliveryIsCod ? 'Za pobraniem' : 'Przelew'],
                                ['Waluta', currency, true]
                            ])
                        ]
                    }
                ],
                columnGap: 10
            },

            // Sekcja 4 - pozycje
            sectionHeader('4. ZESTAWIENIE POZYCJI'),
            {
                table: {
                    headerRows: 1,
                    widths: [18, '*', 30, 24, 45, 50, 28, 45, 50],
                    body: itemsBody
                },
                layout: {
                    hLineWidth: () => 0.5,
                    vLineWidth: () => 0.5,
                    hLineColor: () => '#cbd5e1',
                    vLineColor: () => '#cbd5e1',
                    paddingLeft: () => 3, paddingRight: () => 3, paddingTop: () => 2, paddingBottom: () => 2
                }
            },

            // Sekcja 5 - podsumowanie
            sectionHeader('5. PODSUMOWANIE FINANSOWE'),
            {
                columns: [
                    { width: '33%', stack: [{ text: 'WARTOŚĆ NETTO', fontSize: 8, color: '#6b7280' }, { text: fmtMoney(nettoTotal) + ' ' + currency, fontSize: 13, bold: true }] },
                    { width: '33%', stack: [{ text: 'WARTOŚĆ VAT', fontSize: 8, color: '#6b7280' }, { text: fmtMoney(vatTotal) + ' ' + currency, fontSize: 13, bold: true }] },
                    { width: '34%', stack: [{ text: 'WARTOŚĆ BRUTTO', fontSize: 8, color: '#6b7280' }, { text: fmtMoney(bruttoTotal) + ' ' + currency, fontSize: 13, bold: true, color: NAVY }] }
                ],
                margin: [0, 2, 0, 4]
            },
            { text: 'Słownie: ' + amountInWords(bruttoTotal), fontSize: 9, italics: true, color: '#374151' },

            // Sekcje 6 i 7 obok siebie
            {
                columns: [
                    {
                        width: '58%',
                        stack: [
                            sectionHeader('6. UWAGI / DODATKOWE USTALENIA'),
                            ...buildNotes(order)
                        ]
                    },
                    {
                        width: '42%',
                        stack: [
                            sectionHeader('7. DOKUMENTACJA / QR'),
                            { text: 'Pełna dokumentacja zamówienia dostępna po zeskanowaniu kodu QR (dostęp tylko dla zalogowanych pracowników):', fontSize: 8, color: '#374151', margin: [0, 0, 0, 6] },
                            qrDataUrl
                                ? { image: qrDataUrl, width: 110, alignment: 'center' }
                                : { text: '[ kod QR niedostępny ]', fontSize: 8, alignment: 'center', color: '#9ca3af' },
                            { text: 'Zeskanuj, aby otworzyć kartę zamówienia w systemie ERP', fontSize: 7, alignment: 'center', color: '#6b7280', margin: [0, 4, 0, 0] }
                        ]
                    }
                ],
                columnGap: 10
            },

            // Sekcja 8 - akceptacja
            sectionHeader('8. AKCEPTACJA'),
            {
                columns: [
                    signatureBox('Wystawił', options.issuerName || ''),
                    signatureBox('Sprawdził / Zatwierdził', ''),
                    signatureBox('Akceptacja Klienta', '')
                ],
                columnGap: 10,
                margin: [0, 10, 0, 0]
            }
        ],
        defaultStyle: { font: 'Roboto', fontSize: 10 }
    };

    return new Promise((resolve) => {
        const pdfDoc = pdfMake.createPdf(docDefinition);
        pdfDoc.getBlob((blob) => resolve(blob));
    });
}

function buildNotes(order) {
    const notes = (order.notes || []).filter(n => n && n.content);
    if (!notes.length) return [{ text: 'Brak uwag.', fontSize: 9, color: '#9ca3af' }];
    return notes.slice(0, 15).map(n => ({ text: '• ' + n.content, fontSize: 9, margin: [0, 0, 0, 2] }));
}

function signatureBox(title, name) {
    return {
        width: '33%',
        stack: [
            { text: title, fontSize: 9, bold: true, margin: [0, 0, 0, 22] },
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 150, y2: 0, lineWidth: 0.5, lineColor: '#9ca3af' }] },
            { text: name || '(imię i nazwisko, podpis)', fontSize: 7, color: '#6b7280', margin: [0, 2, 0, 6] },
            { text: 'Data: ........................', fontSize: 8, color: '#374151' }
        ]
    };
}
